import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import {
  type CompanyMoveCheckParameters,
  type CompanyMoveCheckSearchParams,
} from './types';
import {
  zodCompanyMoveCheckPathSchema,
  zodCompanyMoveCheckResponseSchema,
  zodCompanyMoveCheckSearchParamsSchema,
} from './validations';

/**
 * Checks whether a company can move, optionally to a specific company account.
 *
 * Without a target, the checks that need one are skipped, so `movable: true`
 * only means that nothing on the company itself blocks the move.
 *
 * @param parameters - The company ID and the optional target company-account ID.
 * @param options - Additional request options, such as an abort signal.
 * @returns Whether the move is allowed and any blockers.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/check_company_move_vadmin_companies__company_id__move_check_get
 */
export async function fetchCompanyMoveCheck(
  { companyId, targetCompanyAccountId }: CompanyMoveCheckParameters,
  options?: KyRequestOptions,
) {
  const searchParams: CompanyMoveCheckSearchParams = {
    targetCompanyAccountId,
  };

  const response = await vestigasApi
    .get(ENDPOINT.COMPANY.MOVE_CHECK(companyId), {
      searchParams,
      ...options,
      validate: {
        ...options?.validate,
        pathSegments: zodCompanyMoveCheckPathSchema,
        response: zodCompanyMoveCheckResponseSchema,
        searchParams: zodCompanyMoveCheckSearchParamsSchema,
      },
    })
    .json<unknown>();

  return zodCompanyMoveCheckResponseSchema.parse(response);
}
